import React from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import Item from "src/components/wizard/Item/Item";
import StorageLocationAutocomplete from "src/views/capture/RackLocations/StorageLocationAutocomplete/StorageLocationAutocomplete";
import ItemInfo from "./ItemInfo";

const DESTINATION_OPTIONS = [
  {
    value: "original",
    title: "Return to original locations",
    description: "Return each picked item to the location it was picked from.",
  },
  {
    value: "shared",
    title: "Return all to one location",
    description: "Send every selected picked item to the same staging or storage location.",
  },
  {
    value: "individual",
    title: "Choose a location for each item",
    description: "Assign a separate return location to each selected picked item.",
  },
];

export function createEmptyLocation() {
  return {
    warehouse: null,
    warehouse_locations: [],
    mobile_warehouse_location: null,
  };
}

function itemProjectId(item) {
  return item.project?.id || item.projectId;
}

function DestinationOption({ option, selected, onSelect }) {
  return (
    <Paper
      variant="outlined"
      onClick={() => onSelect(option.value)}
      sx={{
        p: 1.5,
        cursor: "pointer",
        borderColor: selected ? "primary.main" : "divider",
        borderWidth: selected ? 2 : 1,
        backgroundColor: selected ? "rgba(25, 118, 210, 0.06)" : "background.paper",
      }}
    >
      <FormControlLabel
        value={option.value}
        control={<Radio />}
        label={
          <Box>
            <Typography fontWeight={500}>{option.title}</Typography>
            <Typography variant="body2" color="textSecondary">
              {option.description}
            </Typography>
          </Box>
        }
        sx={{ m: 0, width: "100%" }}
      />
    </Paper>
  );
}

export function ReturnSelectionStep({
  allSelected,
  companionCount,
  filteredItems,
  onToggleAll,
  onToggleItem,
  returnableItemCount,
  searchTerm,
  selectedIds,
  wouldEmptyOrder,
}) {
  return (
    <>
      <Box px={2} pt={1.5}>
        {companionCount > 0 && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            Required companion items are returned automatically with their parent item.
          </Alert>
        )}
      </Box>
      <Box px={2} pb={1} display="flex" justifyContent="flex-end">
        <Button size="small" onClick={onToggleAll} disabled={returnableItemCount === 0}>
          {allSelected ? "Clear selection" : "Select all"}
        </Button>
      </Box>
      <Box px={2} sx={{ overflowY: "auto", flex: 1 }}>
        {filteredItems.length === 0 ? (
          <Box py={6} textAlign="center">
            <Typography variant="body2" color="textSecondary">
              {searchTerm ? `No items found matching "${searchTerm}"` : "No items can be returned."}
            </Typography>
          </Box>
        ) : (
          filteredItems.map((item) => (
            <Item
              key={item.id}
              value={item}
              selectable
              checked={selectedIds.includes(item.id)}
              selected={selectedIds.includes(item.id)}
              onClick={() => onToggleItem(item.id)}
              info={
                <Box display="flex" alignItems="center" gap={1}>
                  <ItemInfo value={item} />
                  <Typography variant="body2">Quantity: {item.quantity}</Typography>
                </Box>
              }
            />
          ))
        )}
      </Box>
      {wouldEmptyOrder && (
        <Box px={2} pt={1}>
          <Alert severity="warning">
            Keep at least one item on the order, or cancel the order instead.
          </Alert>
        </Box>
      )}
    </>
  );
}

export function ReturnDestinationStep({
  destinationMode,
  itemLocations,
  onDestinationModeChange,
  onItemLocationChange,
  onSharedLocationChange,
  saving,
  selectedPickedItems,
  sharedLocation,
}) {
  const selectedPickedCount = selectedPickedItems.length;

  return (
    <Box px={2} py={2} sx={{ overflowY: "auto", flex: 1 }}>
      <Typography fontSize={18} fontWeight={500} textAlign="center" mb={0.5}>
        Where are the picked items being returned?
      </Typography>
      <Typography variant="body2" color="textSecondary" textAlign="center" mb={2}>
        This location choice applies only to the {selectedPickedCount} selected picked item
        {selectedPickedCount === 1 ? "" : "s"}.
      </Typography>
      <RadioGroup
        value={destinationMode}
        onChange={(event) => onDestinationModeChange(event.target.value)}
        sx={{ gap: 1 }}
      >
        {DESTINATION_OPTIONS.map((option) => (
          <DestinationOption
            key={option.value}
            option={option}
            selected={destinationMode === option.value}
            onSelect={onDestinationModeChange}
          />
        ))}
      </RadioGroup>
      {destinationMode === "shared" && (
        <Box mt={2}>
          <StorageLocationAutocomplete
            value={sharedLocation}
            projectId={itemProjectId(selectedPickedItems[0])}
            onChange={onSharedLocationChange}
            disabled={saving}
          />
        </Box>
      )}
      {destinationMode === "individual" && (
        <Box mt={2} display="flex" flexDirection="column" gap={2}>
          {selectedPickedItems.map((item) => (
            <Paper key={item.id} variant="outlined" sx={{ p: 1.5 }}>
              <Item value={item} stackOnMobile={false} />
              <Box mt={1}>
                <StorageLocationAutocomplete
                  value={itemLocations[item.id] || createEmptyLocation()}
                  projectId={itemProjectId(item)}
                  onChange={(values) => onItemLocationChange(item.id, values)}
                  disabled={saving}
                />
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
}

export function ReturnConfirmStep({
  destinationSummary,
  onReasonChange,
  reason,
  returnedCount,
  saving,
  selectedCount,
  selectedPickedCount,
}) {
  return (
    <Box px={2} py={2} sx={{ overflowY: "auto", flex: 1 }}>
      <Typography fontSize={18} fontWeight={500} mb={2}>
        Review return
      </Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography>
          <Box component="span" fontWeight={500}>
            Items:
          </Box>{" "}
          {selectedCount} total ({selectedPickedCount} picked, {selectedCount - selectedPickedCount}{" "}
          not picked)
        </Typography>
        {selectedPickedCount > 0 && (
          <Typography mt={0.75}>
            <Box component="span" fontWeight={500}>
              Return location:
            </Box>{" "}
            {destinationSummary}
          </Typography>
        )}
      </Paper>
      <TextField
        fullWidth
        required
        multiline
        minRows={3}
        label="Reason for return"
        placeholder="Why did the customer's request change?"
        value={reason}
        onChange={(event) => onReasonChange(event.target.value)}
        disabled={saving}
        helperText="This will be added to the order's Final Release notes."
      />
      {saving && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Returning {Math.min(returnedCount + 1, selectedCount)} of {selectedCount} items…
        </Alert>
      )}
    </Box>
  );
}
